import axios from "axios";
import { ApiBaseUrl } from "@/constants";
import apiController from "@/utils/apiController";
import permissionsManifest from "@/permissions.manifest.json";
import { getScreenRoute } from "@/utils/screenRouteMap";

export const TIPO_RECURSO = {
  ENDPOINT: 1,
  TELA: 2,
  EMPRESA: 3,
  UNIDADE: 4,
  DASHBOARD_BI: 5,
};

export const PERMISSION_TYPES = {
  SCREEN: "screen",
  COMPANY: "company",
  UNIT: "unit",
  DASHBOARD: "dashboard",
  INTEGRATION: "integration",
};

const TIPO_BY_NAME = {
  Endpoint: PERMISSION_TYPES.INTEGRATION,
  Tela: PERMISSION_TYPES.SCREEN,
  Empresa: PERMISSION_TYPES.COMPANY,
  Unidade: PERMISSION_TYPES.UNIT,
  DashboardBI: PERMISSION_TYPES.DASHBOARD,
};

export function endpointCodFromKey(endpointKey) {
  const registered = apiController.endpoints[endpointKey];
  if (registered && registered.resource) {
    return registered.resource;
  }
  return String(endpointKey).toUpperCase();
}

export function inferPermissionType(recursos) {
  if (!recursos || recursos.length === 0) return null;

  const types = new Set(
    recursos.map((r) => TIPO_BY_NAME[r.descricao_tipo_recurso]).filter(Boolean)
  );

  if (types.has(PERMISSION_TYPES.SCREEN)) return PERMISSION_TYPES.SCREEN;
  if (types.size === 1) return [...types][0];
  if (types.has(PERMISSION_TYPES.INTEGRATION) && !types.has(PERMISSION_TYPES.SCREEN)) {
    return PERMISSION_TYPES.INTEGRATION;
  }
  return PERMISSION_TYPES.INTEGRATION;
}

function findByEmpresa(recursos, idEmpresa) {
  return recursos.find(
    (r) =>
      r.descricao_tipo_recurso === "Empresa" &&
      Number(r.id_empresa) === Number(idEmpresa)
  );
}

function findByUnidade(recursos, idUnidade) {
  return recursos.find(
    (r) =>
      r.descricao_tipo_recurso === "Unidade" &&
      Number(r.id_unidade) === Number(idUnidade)
  );
}

function findByDashboard(recursos, idDashboard) {
  return recursos.find(
    (r) =>
      r.descricao_tipo_recurso === "DashboardBI" &&
      Number(r.id_dashboard) === Number(idDashboard)
  );
}

function findTelaByNome(recursos, screenName) {
  return recursos.find(
    (r) => r.descricao_tipo_recurso === "Tela" && r.nome === screenName
  );
}

function findEndpointByCod(recursos, codRecurso) {
  return recursos.find(
    (r) =>
      r.descricao_tipo_recurso === "Endpoint" &&
      r.cod_recurso === codRecurso
  );
}

async function insertRecurso(token, payload) {
  const response = await axios.post(
    ApiBaseUrl + "/api/v1.0/insertrecurso",
    payload,
    { headers: { "x-access-token": token } }
  );
  if (response.data.erro) {
    throw new Error(response.data.mensagem || "Erro ao criar recurso");
  }
  return {
    id_recurso: response.data.insertId,
    ...payload,
    descricao_tipo_recurso: tipoLabel(payload.id_tipo_recurso),
  };
}

function tipoLabel(idTipo) {
  const map = {
    [TIPO_RECURSO.ENDPOINT]: "Endpoint",
    [TIPO_RECURSO.TELA]: "Tela",
    [TIPO_RECURSO.EMPRESA]: "Empresa",
    [TIPO_RECURSO.UNIDADE]: "Unidade",
    [TIPO_RECURSO.DASHBOARD_BI]: "DashboardBI",
  };
  return map[idTipo] || "";
}

export async function ensureResource(token, recursosCache, payload) {
  let existing = null;

  if (payload.id_tipo_recurso === TIPO_RECURSO.EMPRESA) {
    existing = findByEmpresa(recursosCache, payload.id_empresa);
  } else if (payload.id_tipo_recurso === TIPO_RECURSO.UNIDADE) {
    existing = findByUnidade(recursosCache, payload.id_unidade);
  } else if (payload.id_tipo_recurso === TIPO_RECURSO.DASHBOARD_BI) {
    existing = findByDashboard(recursosCache, payload.id_dashboard);
  } else if (payload.id_tipo_recurso === TIPO_RECURSO.TELA) {
    existing = findTelaByNome(recursosCache, payload.nome);
    if (!existing) {
      existing = recursosCache.find(
        (r) =>
          r.descricao_tipo_recurso === "Tela" &&
          r.cod_recurso === payload.cod_recurso
      );
    }
  }

  if (existing) return existing;

  const created = await insertRecurso(token, payload);
  recursosCache.push(created);
  return created;
}

export async function resolveEmpresaResources(token, recursosCache, empresas) {
  const resolved = [];
  for (const empresa of empresas) {
    const id = empresa.id_empresa;
    const recurso = await ensureResource(token, recursosCache, {
      rota: `empresa/${id}`,
      cod_recurso: `EMPRESA/${id}`,
      id_tipo_recurso: TIPO_RECURSO.EMPRESA,
      id_empresa: id,
      id_unidade: null,
      nome: null,
      id_dashboard: null,
      razao_social: empresa.razao_social,
    });
    resolved.push(recurso);
  }
  return { resolved, missing: [] };
}

export async function resolveUnidadeResources(token, recursosCache, unidades) {
  const resolved = [];
  for (const unidade of unidades) {
    const id = unidade.id_unidade;
    const recurso = await ensureResource(token, recursosCache, {
      rota: `unidade/${id}`,
      cod_recurso: `UNIDADE/${id}`,
      id_tipo_recurso: TIPO_RECURSO.UNIDADE,
      id_empresa: null,
      id_unidade: id,
      nome: null,
      id_dashboard: null,
      cod_unidade: unidade.cod_unidade,
      descricao_unidade: unidade.descricao_unidade,
    });
    resolved.push(recurso);
  }
  return { resolved, missing: [] };
}

export async function resolveDashboardResources(token, recursosCache, dashboards) {
  const resolved = [];
  for (const dashboard of dashboards) {
    const id = dashboard.id_dashboard;
    const cod = dashboard.cod_dashboard || "";
    const recurso = await ensureResource(token, recursosCache, {
      rota: cod,
      cod_recurso: `BI${cod.toUpperCase()}`,
      id_tipo_recurso: TIPO_RECURSO.DASHBOARD_BI,
      id_empresa: null,
      id_unidade: null,
      nome: null,
      id_dashboard: id,
      cod_dashboard: cod,
      titulo_dashboard: dashboard.titulo_dashboard,
    });
    resolved.push(recurso);
  }
  return { resolved, missing: [] };
}

export async function resolveScreenResources(token, recursosCache, screenName) {
  const screenMeta = permissionsManifest.screens.find((s) => s.name === screenName);
  const endpointKeys = screenMeta ? screenMeta.endpointKeys || [] : [];
  const missing = [];
  const resolved = [];

  const routeInfo = getScreenRoute(screenName, recursosCache);
  const existingTela = findTelaByNome(recursosCache, screenName);
  const codRecurso =
    existingTela?.cod_recurso || screenName.replace(/[^a-zA-Z0-9]/g, "").toUpperCase() || screenName.toUpperCase();
  const rota = existingTela?.rota || routeInfo?.path || `/${screenName.toLowerCase()}`;

  const tela = await ensureResource(token, recursosCache, {
    rota,
    cod_recurso: codRecurso,
    id_tipo_recurso: TIPO_RECURSO.TELA,
    id_empresa: null,
    id_unidade: null,
    nome: screenName,
    id_dashboard: null,
  });
  resolved.push(tela);

  const endpointRecursos = recursosCache.filter(
    (r) => r.descricao_tipo_recurso === "Endpoint"
  );

  for (const key of endpointKeys) {
    const cod = endpointCodFromKey(key);
    const endpoint = findEndpointByCod(endpointRecursos, cod);
    if (endpoint) {
      if (!resolved.some((r) => r.id_recurso === endpoint.id_recurso)) {
        resolved.push(endpoint);
      }
    } else {
      missing.push(key);
    }
  }

  return { resolved, missing };
}

export function resolveIntegrationResources(recursosCache, selectedIds) {
  const resolved = recursosCache.filter(
    (r) =>
      r.descricao_tipo_recurso === "Endpoint" &&
      selectedIds.includes(r.id_recurso)
  );
  return { resolved, missing: [] };
}

export function getManifestScreens() {
  return (permissionsManifest.screens || [])
    .map((s) => s.name)
    .sort((a, b) => a.localeCompare(b));
}

export function dedupeResources(recursos) {
  const seen = new Set();
  return recursos.filter((r) => {
    const id = r.id_recurso;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}